import { DateTimePickerRangesAll } from "../config/enums";
import { getDateRangeDateValues } from "./dateRangeValues";
import { FilterNode } from "./advancedSearch.types";

/** One end of a date rule, in the shape the legacy date filters held and submit reads. */
export interface DateBound {
  Key: string;
  DateTimeRange: number | null;
  Value: Date | null;
  Type: "datetime";
  SetInitially: boolean;
}

export const DateRange = DateTimePickerRangesAll as Record<string, number>;

export const dateRangeOptions = Object.entries(DateRange).map(
  ([Value, Key]) => ({ Key, Value })
);

export const SingleDateRanges = [
  DateRange.On,
  DateRange["On or After"],
  DateRange["On or Before"],
];

const bound = (
  rule: FilterNode,
  end: "From" | "To",
  range: number | null,
  value: Date | null
): DateBound => ({
  Key: `${rule.name}-${end}`,
  DateTimeRange: range,
  Value: value,
  Type: "datetime",
  SetInitially: false,
});

export function getDateBounds(rule: FilterNode) {
  const bounds = Array.isArray(rule.value) ? (rule.value as DateBound[]) : [];
  const from = bounds.find((b) => b.Key === `${rule.name}-From`);
  const to = bounds.find((b) => b.Key === `${rule.name}-To`);

  return {
    range: from?.DateTimeRange ?? to?.DateTimeRange ?? null,
    from: from?.Value ?? null,
    to: to?.Value ?? null,
  };
}

/** A `yyyy-MM-dd` day as local midnight, so it is not shifted by the zone. */
export function parseDay(value: string): Date | null {
  const [year, month, day] = value.split("-").map(Number);

  return year && month && day ? new Date(year, month - 1, day) : null;
}

const startOfDay = (date: Date) => new Date(new Date(date).setHours(0, 0, 0, 0));
const endOfDay = (date: Date) => new Date(new Date(date).setHours(23, 59, 59, 999));

/** The rule's value once a range is picked: the relative ranges fill both ends. */
export function dateValueForRange(rule: FilterNode, range: number | null) {
  if (!range) {
    return "";
  }

  const needsDates =
    range === DateRange.Custom || SingleDateRanges.includes(range);
  const [from, to] = needsDates
    ? [null, null]
    : (getDateRangeDateValues(range) as [Date, Date]);

  return [bound(rule, "From", range, from), bound(rule, "To", range, to)];
}

/** The value after the day of an On / On or After / On or Before range is picked. */
export function dateValueForDay(rule: FilterNode, range: number, day: Date | null) {
  const from = day && range !== DateRange["On or Before"] ? startOfDay(day) : null;
  const to = day && range !== DateRange["On or After"] ? endOfDay(day) : null;

  return [bound(rule, "From", range, from), bound(rule, "To", range, to)];
}

/** The value after one end of a custom range changes; the To end runs to the end of its day. */
export function dateValueForCustom(
  rule: FilterNode,
  end: "From" | "To",
  day: Date | null
) {
  const { from, to } = getDateBounds(rule);

  return [
    bound(rule, "From", DateRange.Custom, end === "From" && day ? startOfDay(day) : end === "From" ? null : from),
    bound(rule, "To", DateRange.Custom, end === "To" && day ? endOfDay(day) : end === "To" ? null : to),
  ];
}
